import React from 'react';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
// graphQL
import gql from 'graphql-tag';
// adopt
import { adopt } from 'react-adopt';
// our components
import { PERMISSION } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { Query, Mutation } from 'components/common/Query';
import PermissionGroupForm from 'components/Permission/Form';

/**
 * Query to read a permission group
 */
export const PERMISSION_GROUP_QUERY = gql`
  query PermissionGroup($id: Int!) {
    permissionsGroup(id: $id) {
      id
      name
      permissions {
        id
        codename
        name
      }
    }
  }
`;

/**
 * Mutation to create a permissions group
 */
export const UPDATE_PERMISSIONS_GROUP_MUTATION = gql`
  mutation($id: ID!, $name: String!, $permissions: [ID]!) {
    updatePermissionGroup(
      input: { id: $id, name: $name, permissions: $permissions }
    ) {
      group {
        id
        name
      }
      errors {
        field
        messages
      }
    }
  }
`;

class PermissionGroupUpdateContainer extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      errors: [],
    };
    this.Compose = adopt({
      mutatePermissionUpdate: this.permissionUpdate,
      query: this.permissionGroup,
    });
  }

  /**
   * @description
   * User's query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   */
  permissionGroup = ({ render }) => {
    const { id } = this.props;
    return (
      <Query
        query={PERMISSION_GROUP_QUERY}
        variables={{ id }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading }) => render({ data, loading })}
      </Query>
    );
  };

  /**
   * @description
   * Mutation update to compose.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  permissionUpdate = ({ render }) => (
    <Mutation
      mutation={UPDATE_PERMISSIONS_GROUP_MUTATION}
      onCompleted={this.permissionUpdateCompleted}
    >
      {(handlePermissionUpdate, { loading }) =>
        render({ handlePermissionUpdate, submitLoading: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Callback called when mutation is completed.
   *
   * @param {Object} obj - Returned data.
   * */
  permissionUpdateCompleted = async ({ updatePermissionGroup }) => {
    const { errors, group } = updatePermissionGroup;
    const history = this.context;
    if (errors) {
      this.setState({ errors });
    } else {
      history.push(formatRoute(PERMISSION.DETAILS, { id: group.id }));
    }
  };

  /**
   * @description
   * Render PermissionGroupForm component.
   *
   * @returns {*} PermissionGroupForm component.
   */
  render() {
    const { errors } = this.state;
    const { id } = this.props;
    return (
      <this.Compose>
        {({
          mutatePermissionUpdate: { handlePermissionUpdate, submitLoading },
          query: { data, loading },
        }) => {
          if (!loading) {
            return (
              <PermissionGroupForm
                editMode
                loading={submitLoading}
                name={data && data.permissionsGroup.name}
                selectedPermissions={data && data.permissionsGroup.permissions}
                permissionSave={result => {
                  handlePermissionUpdate({ variables: { id, ...result } });
                }}
                errors={errors}
              />
            );
          }
          return (
            <ContentLoader height={491} width={650} speed={2}>
              <path
                // eslint-disable-next-line max-len
                d="M8,67 L258,67 C262.418278,67 266,70.581722 266,75 L266,483 C266,487.418278 262.418278,491 258,491 L8,491 C3.581722,491 5.41083001e-16,487.418278 0,483 L0,75 C-5.41083001e-16,70.581722 3.581722,67 8,67 Z M5.5,0 L123.5,0 C126.537566,-5.57991845e-16 129,2.46243388 129,5.5 C129,8.53756612 126.537566,11 123.5,11 L5.5,11 C2.46243388,11 3.71994563e-16,8.53756612 0,5.5 C-3.71994563e-16,2.46243388 2.46243388,5.57991845e-16 5.5,0 Z M8,17 L258,17 C262.418278,17 266,20.581722 266,25 L266,34 C266,38.418278 262.418278,42 258,42 L8,42 C3.581722,42 5.41083001e-16,38.418278 0,34 L0,25 C-5.41083001e-16,20.581722 3.581722,17 8,17 Z M391,67 L641,67 C645.418278,67 649,70.581722 649,75 L649,483 C649,487.418278 645.418278,491 641,491 L391,491 C386.581722,491 383,487.418278 383,483 L383,75 C383,70.581722 386.581722,67 391,67 Z M300,258 L350,258 C354.418278,258 358,261.581722 358,266 L358,292 C358,296.418278 354.418278,300 350,300 L300,300 C295.581722,300 292,296.418278 292,292 L292,266 C292,261.581722 295.581722,258 300,258 Z"
                fill="#D8D8D8"
              />
            </ContentLoader>
          );
        }}
      </this.Compose>
    );
  }
}

/**
 * PermissionGroupUpdateContainer PropTypes
 * @param id - prop to know id of the PermissionGroup
 */

PermissionGroupUpdateContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

export default PermissionGroupUpdateContainer;
